import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, MenuIcon, PhoneIcon } from 'lucide-react';
import { mainNav } from '../../data/navigation';
import { BOOKING_URL, SITE } from '../../data/site';
import { Logo } from '../ui/Logo';
import { MegaMenu } from './MegaMenu';
import { MobileNav } from './MobileNav';
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);
  const location = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    setMegaOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMegaOpen(false);
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
  }, [mobileOpen]);
  const openMega = () => {
    window.clearTimeout(closeTimer.current);
    setMegaOpen(true);
  };
  const scheduleClose = () => {
    closeTimer.current = window.setTimeout(() => setMegaOpen(false), 120);
  };
  const solid = scrolled || megaOpen;
  const navLinkCls = ({
    isActive
  }: {
    isActive: boolean;
  }) => `relative whitespace-nowrap py-2 font-display text-[14px] font-medium uppercase tracking-[0.06em] transition-colors duration-150 ${isActive ? 'text-white after:absolute after:inset-x-0 after:-bottom-0.5 after:h-0.5 after:bg-accent' : 'text-chrome-200 hover:text-white'}`;
  return <header className={`sticky top-0 z-50 transition-[background-color,box-shadow] duration-200 ease-out ${solid ? 'bg-navy/90 shadow-[0_8px_24px_rgba(0,0,0,0.25)] backdrop-blur-md' : 'bg-transparent'}`} data-id="element-363" data-mp="35dtlj1mmo3z4">
      <div className="gutter flex h-[var(--header-h)] items-center justify-between gap-6" data-id="element-364" data-mp="3ef9x3cp6ic4x">
        <Logo data-id="element-365" data-mp="2qpfioqp7ofik" />
        <nav aria-label="Main" className="hidden items-center gap-5 nav:flex" data-id="element-366" data-mp="2qoxen7wb4x03">
          <div onMouseEnter={openMega} onMouseLeave={scheduleClose} data-id="element-367" data-mp="2mnu14s1qfd21">
            <button type="button" aria-expanded={megaOpen} aria-controls="mega-menu" onClick={() => setMegaOpen(v => !v)} className="flex items-center gap-1 whitespace-nowrap py-2 font-display text-[14px] font-medium uppercase tracking-[0.06em] text-chrome-200 hover:text-white" data-id="element-368" data-mp="30dxknb988sns">
              Courses
              <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${megaOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-369" data-mp="igso3d99ialj" />
            </button>
          </div>
          {mainNav.map(item => <NavLink key={item.to} to={item.to} className={navLinkCls} data-id="element-370" data-mp="9gmdbz4y7oq2">
              {item.label}
            </NavLink>)}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3" data-id="element-371" data-mp="efp4qgs09wfk">
          <a href={SITE.phoneHref} className="hidden items-center gap-2 whitespace-nowrap font-display text-sm font-medium tracking-wider text-white lg:flex nav:hidden" data-id="element-372" data-mp="1248cmv758774">
            <PhoneIcon className="h-4 w-4 text-accent-bright" aria-hidden="true" data-id="element-373" data-mp="s7dggiyceiw0" />
            {SITE.phoneDisplay}
          </a>
          <a href={BOOKING_URL} className="hidden h-11 items-center whitespace-nowrap rounded-[3px] bg-accent px-5 font-display text-[13px] font-semibold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-accent-bright sm:inline-flex" data-id="element-374" data-mp="x43yvcxmdqkl">
            Book a Class
          </a>
          <button type="button" onClick={() => setMobileOpen(true)} className="flex h-11 w-11 items-center justify-center rounded-sm text-white nav:hidden" aria-label="Open menu" aria-expanded={mobileOpen} data-id="element-375" data-mp="3seugb3k2fvff">
            <MenuIcon className="h-6 w-6" aria-hidden="true" data-id="element-376" data-mp="3jc1p10avvjpl" />
          </button>
        </div>
      </div>
      <div onMouseEnter={openMega} onMouseLeave={scheduleClose} className="hidden nav:block" data-id="element-377" data-mp="jwwuq3g3newi">
        <AnimatePresence data-id="element-378" data-mp="fvbce2ksq8ex">{megaOpen && <MegaMenu id="mega-menu" onNavigate={() => setMegaOpen(false)} data-id="element-379" data-mp="ay2ve5bdwaf9" />}</AnimatePresence>
      </div>
      {createPortal(<AnimatePresence data-id="element-380" data-mp="5y6pg0j4m8x2">{mobileOpen && <MobileNav onClose={() => setMobileOpen(false)} data-id="element-381" data-mp="5z00vet15mlj" />}</AnimatePresence>, document.body)}
    </header>;
}